import { t } from "@lingui/macro"
import React, { useCallback } from "react"
import { observer } from "mobx-react"
import { useNavigate } from "react-router-dom"
import { Box, Typography, Grid, Button } from "@mui/material"

import { AsyncRequestItemDetailModalStore } from "./store"
import { IAsyncRequestItemDetailModalProps } from "./types"

import { ModalHeader } from "src/components/ModalHeader"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { Time } from "src/components/Time"
import { StatusChip } from "src/components/StatusChip"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { useInitializer } from "src/lib/initializer"
import { parseDate } from "src/lib/date"
import { avy_api_pkg_async_AsyncStatus } from "src/api/models/avy_api_pkg_async_AsyncStatus"
import { GlobalStore } from "src/store"

const Modal = observer(({ id }: IAsyncRequestItemDetailModalProps) => {
    const store = useStore(AsyncRequestItemDetailModalStore)
    const globalStore = useStore(GlobalStore)
    const navigate = useNavigate()

    const initialized = useInitializer(async () => {
        await store.init(id)
    }, [id])

    const handleViewParentRequestClick = useCallback(() => {
        if (
            store.asyncRequestItem?.async_request_id != null &&
            store.asyncRequestItem.async_request_id > 0
        ) {
            globalStore.modals.pop()
            navigate(
                `/async-requests?id=${store.asyncRequestItem.async_request_id}`,
            )
        }
    }, [globalStore.modals, navigate, store.asyncRequestItem?.async_request_id])

    const getStatusChipState = (
        status: avy_api_pkg_async_AsyncStatus,
    ): "positive" | "neutral" | "negative" | "info" | "other" | "dark" => {
        switch (status) {
            case "completed":
                return "positive"
            case "failed":
                return "negative"
            case "processing":
                return "neutral"
            case "partial":
                return "neutral"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "other"
        }
    }

    const getStatusText = (status: avy_api_pkg_async_AsyncStatus): string => {
        switch (status) {
            case "created":
                return t`async-request-items.status.created`
            case "pending":
                return t`async-request-items.status.pending`
            case "pending_send":
                return t`async-request-items.status.pending-send`
            case "sent":
                return t`async-request-items.status.sent`
            case "processing":
                return t`async-request-items.status.processing`
            case "completed":
                return t`async-request-items.status.completed`
            case "failed":
                return t`async-request-items.status.failed`
            case "partial":
                return t`async-request-items.status.partial`
            default:
                return status
        }
    }

    if (!initialized || store.asyncRequestItem == null) {
        return <CenteredSpinner height="45vh" />
    }

    const asyncRequestItem = store.asyncRequestItem

    return (
        <>
            <ModalHeader>
                <Typography variant="h6">
                    {t`async-request-item-detail.title`} #{asyncRequestItem.id}
                </Typography>
            </ModalHeader>

            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.async-request-id`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {asyncRequestItem.async_request_id ?? "-"}
                        </Typography>

                        {Boolean(asyncRequestItem.async_request_id) && (
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    onClick={handleViewParentRequestClick}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                >
                                    {t`async-request-item-detail.view-request`}
                                </Button>
                            </Box>
                        )}

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.status`}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <StatusChip
                                label={getStatusText(
                                    asyncRequestItem.status ?? "created",
                                )}
                                state={getStatusChipState(
                                    asyncRequestItem.status ?? "created",
                                )}
                            />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.attempts`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequestItem.attempts}/
                            {asyncRequestItem.max_attempts}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.worker-id`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequestItem.worker_id ?? "-"}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.payload-hash`}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 2, wordBreak: "break-all" }}
                        >
                            {asyncRequestItem.payload_hash ?? "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.created-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequestItem.created_at) ? (
                                <Time
                                    date={parseDate(
                                        asyncRequestItem.created_at,
                                    )}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.sent-to-pubsub-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequestItem.sent_to_pubsub_at) ? (
                                <Time
                                    date={parseDate(
                                        asyncRequestItem.sent_to_pubsub_at,
                                    )}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.processed-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequestItem.processed_at) ? (
                                <Time
                                    date={parseDate(
                                        asyncRequestItem.processed_at,
                                    )}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.pubsub-message-id`}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 2, wordBreak: "break-all" }}
                        >
                            {asyncRequestItem.pubsub_message_id ?? "-"}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-item-detail.pubsub-details`}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>
                                    {t`async-request-item-detail.project-id`}:
                                </strong>{" "}
                                {asyncRequestItem.pubsub_project_id ?? "-"}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>
                                    {t`async-request-item-detail.topic`}:
                                </strong>{" "}
                                {asyncRequestItem.pubsub_topic ?? "-"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>
                                    {t`async-request-item-detail.subscription`}:
                                </strong>{" "}
                                {asyncRequestItem.pubsub_subscription ?? "-"}
                            </Typography>
                        </Box>
                    </Grid>

                    {(Boolean(asyncRequestItem.error_code) ||
                        Boolean(asyncRequestItem.error_message)) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t`async-request-item-detail.error-details`}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: "error.light",
                                    borderRadius: 1,
                                    color: "error.contrastText",
                                }}
                            >
                                {Boolean(asyncRequestItem.error_code) && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>
                                            {t`async-request-item-detail.error-code`}
                                            :
                                        </strong>{" "}
                                        {asyncRequestItem.error_code}
                                    </Typography>
                                )}
                                {Boolean(asyncRequestItem.error_message) && (
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                        <strong>
                                            {t`async-request-item-detail.error-message`}
                                            :
                                        </strong>
                                        <br />
                                        {asyncRequestItem.error_message}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    )}

                    {Boolean(asyncRequestItem.payload) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t`async-request-item-detail.payload`}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: "grey.100",
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: "auto",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {asyncRequestItem.payload}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </>
    )
})

export const AsyncRequestItemDetailModal = (
    props: IAsyncRequestItemDetailModalProps,
) => (
    <StoreProvider Store={AsyncRequestItemDetailModalStore}>
        <Modal {...props} />
    </StoreProvider>
)
